import { Button as ChakraButton } from "@chakra-ui/react";
import { useCachedValue } from "lib/common/use-cached-value";
import { IButtonProps } from "./interfaces";
import { ChildrenCache } from "../children-cache";

/**
 * Button component that fixes issues with Google Translate throwing React errors
 * See https://martijnhols.nl/blog/everything-about-google-translate-crashing-react
 *
 * The "fix" uses two separate buttons, one for the loading state and one for the normal state.
 *
 * This allows Google Translate to modify the DOM for the non-loading state without causing issues
 * when the loading state is displayed.
 */
export function Button(props: IButtonProps) {
  const { isLoading = false } = props;

  // props may change when isLoading: true (e.g. data-testid), so cache accordingly
  const normalProps = useCachedValue({ enableUpdate: !isLoading, value: props });
  const loadingProps = useCachedValue({ enableUpdate: isLoading, value: props });

  /**
   * The non-loading button is NOT unmounted when isLoading === true.
   * It is hidden and disabled and its "id" and "data-testid" attributes are removed.
   * This keeps the translated text in the DOM so that it is not lost, and is
   * visible again when isLoading === false.
   */
  return (
    <>
      {/* render loading button: only when isLoading === true */}
      {isLoading && (
        <ChakraButton
          {...loadingProps.cachedValue}
          isLoading={isLoading}
        />
      )}
      {/* render normal button (hidden and disabled when isLoading === true) */}
      <ChakraButton
        {...normalProps.cachedValue}
        id={!isLoading ? normalProps.cachedValue?.id ?? undefined : undefined}
        data-testid={!isLoading ? (normalProps.cachedValue as Record<string, unknown>)?.["data-testid"] as string ?? undefined : undefined}
        hidden={props.hidden || isLoading}
        isDisabled={props.isDisabled || isLoading}
      >
        {/*
          Support changes to button text being translated and not reverted.
          NOTE: Google Translate may not translate updated text without the user
          toggling translation off and on again.
         */}
        <ChildrenCache cacheEnabled={!isLoading}>
          {normalProps.cachedValue.children}
        </ChildrenCache>
      </ChakraButton>
    </>
  );
}
